import { useEffect, useState, useRef } from "react";
import { Package, Save, X, DollarSign, Boxes, AlertCircle, Tag, ShoppingCart, Plus, Trash2, Image } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

// Menu categories
const CATEGORIES_RESTAURANT = [
  { value: "comida", label: "🍽️ Food" },
  { value: "bebida", label: "🥤 Drink" },
  { value: "postre", label: "🍰 Dessert" },
  { value: "otro",   label: "📦 Other" },
];

const CATEGORIES_DINER = [
  { value: "plato",           label: "🍽️ Entree" },
  { value: "pollo",           label: "🍗 Chicken" },
  { value: "mariscos",        label: "🦐 Seafood" },
  { value: "carnes",          label: "🥩 Meats" },
  { value: "sopas",           label: "🍜 Soups" },
  { value: "especiales",      label: "⭐ Specials" },
  { value: "acompañamientos", label: "🍚 Sides" },
  { value: "bebida",          label: "🥤 Drinks" },
];

export default function ProductForm({ onSaved, editingProduct, onCancel }) {
  const codeRef = useRef(null);
  const photoInputRef = useRef(null);

  const { user } = useAuth();
  const categories = user?.tipo_negocio === "comedor" ? CATEGORIES_DINER : CATEGORIES_RESTAURANT;
  const defaultCategory = user?.tipo_negocio === "comedor" ? "plato" : "comida";

  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  const [salePrice, setSalePrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");

  const [stock, setStock] = useState("");
  const [minStock, setMinStock] = useState(5);
  const [description, setDescription] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);

  const [itemType, setItemType] = useState(2); // 1=Product, 2=Service

  const [category, setCategory] = useState(defaultCategory);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [removeExistingPhoto, setRemoveExistingPhoto] = useState(false);

  const [hasPresentations, setHasPresentations] = useState(false);
  const [presentationType, setPresentationType] = useState("empaque"); // 'empaque' or 'variante'
  const [presentations, setPresentations] = useState([]);

  useEffect(() => {
    if (editingProduct) {
      setCode(editingProduct.codigo || "");
      setName(editingProduct.nombre || "");

      setSalePrice(editingProduct.precio_venta || "");
      setPurchasePrice(editingProduct.precio_compra || "");

      setStock(editingProduct.stock ?? "");
      setMinStock(editingProduct.stock_minimo ?? 5);
      setDescription(editingProduct.descripcion || "");
      setTaxExempt(!editingProduct.is_taxable);
      setItemType(editingProduct.tipo_item || 1);

      setCategory(editingProduct.categoria || defaultCategory);
      setPhotoFile(null);
      setPhotoPreview(
        editingProduct.foto
          ? `https://altivawebusa.com/BACK/public/storage/${editingProduct.foto}`
          : null
      );
      setRemoveExistingPhoto(false);

      const loadPresentations = async () => {
        try {
          const response = await api.get(`/products/${editingProduct.id}`);
          const productData = response.data;

          if (productData.presentations && productData.presentations.length > 0) {
            setHasPresentations(true);

            const firstPresentation = productData.presentations[0];
            setPresentationType(firstPresentation.tipo_presentacion || "empaque");

            setPresentations(
              productData.presentations.map((p) => ({
                id: p.id,
                descripcion: p.descripcion,
                precio_compra: p.precio_compra || "",
                precio_venta: p.precio_venta,
                factor_conversion: p.factor_conversion,
                tipo_presentacion: p.tipo_presentacion || "empaque",
                stock_independiente: p.stock_independiente || "",
              }))
            );
          } else {
            setHasPresentations(false);
            setPresentations([]);
          }
        } catch (error) {
          console.error("Error loading presentations:", error);
          if (editingProduct.presentations && editingProduct.presentations.length > 0) {
            setHasPresentations(true);
            setPresentations(editingProduct.presentations);
          }
        }
      };

      if (editingProduct.tipo_item === 1) {
        loadPresentations();
      }
    } else {
      resetForm();
    }

    setTimeout(() => {
      codeRef.current?.focus();
    }, 100);
  }, [editingProduct]);

  const resetForm = () => {
    setCode("");
    setName("");
    setSalePrice("");
    setPurchasePrice("");
    setStock("");
    setMinStock(5);
    setDescription("");
    setTaxExempt(false);
    setItemType(2);

    setCategory(defaultCategory);
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemoveExistingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = "";

    setHasPresentations(false);
    setPresentationType("empaque");
    setPresentations([]);

    setTimeout(() => {
      codeRef.current?.focus();
    }, 50);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire("Error", "Only image files are allowed (JPG, PNG, etc.)", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("Error", "The image must not exceed 2MB", "error");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemoveExistingPhoto(false);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemoveExistingPhoto(true);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const addPresentation = () => {
    setPresentations([
      ...presentations,
      {
        id: null,
        descripcion: "",
        precio_compra: presentationType === "variante" ? "" : null,
        precio_venta: "",
        factor_conversion: 1,
        tipo_presentacion: presentationType,
        stock_independiente: presentationType === "variante" ? "" : null,
      },
    ]);
  };

  const removePresentation = (index) => {
    setPresentations(presentations.filter((_, i) => i !== index));
  };

  const updatePresentation = (index, field, value) => {
    const updated = [...presentations];
    updated[index][field] = value;
    setPresentations(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (code.trim() && /\s/.test(code)) {
      Swal.fire("Error", "The code must not contain spaces.", "error");
      return;
    }

    if (parseInt(itemType) === 1 && !hasPresentations && !purchasePrice) {
      Swal.fire("Error", "Purchase price is required for Product type items.", "error");
      return;
    }

    if (hasPresentations && presentations.length === 0) {
      Swal.fire("Error", "Add at least one presentation or uncheck the multiple presentations option.", "error");
      return;
    }

    if (hasPresentations) {
      for (let i = 0; i < presentations.length; i++) {
        const pres = presentations[i];

        if (!pres.descripcion.trim()) {
          Swal.fire("Error", `Presentation ${i + 1} needs a description.`, "error");
          return;
        }

        if (presentationType === "variante") {
          if (!pres.precio_compra || parseFloat(pres.precio_compra) < 0) {
            Swal.fire("Error", `Presentation ${i + 1} needs a valid purchase price.`, "error");
            return;
          }
        }

        if (!pres.precio_venta || parseFloat(pres.precio_venta) <= 0) {
          Swal.fire("Error", `Presentation ${i + 1} needs a valid price.`, "error");
          return;
        }

        if (presentationType === "empaque") {
          if (!pres.factor_conversion || parseInt(pres.factor_conversion) < 1) {
            Swal.fire("Error", `Presentation ${i + 1} needs a valid conversion factor.`, "error");
            return;
          }
        } else if (presentationType === "variante") {
          if (pres.stock_independiente === "" || parseInt(pres.stock_independiente) < 0) {
            Swal.fire("Error", `Presentation ${i + 1} needs a valid stock value.`, "error");
            return;
          }
        }
      }
    }

    if (
      !hasPresentations &&
      parseInt(itemType) === 1 &&
      purchasePrice &&
      salePrice &&
      parseFloat(salePrice) < parseFloat(purchasePrice)
    ) {
      const result = await Swal.fire({
        title: "⚠️ Warning",
        text: "The sale price is lower than the purchase price. This will result in a loss. Continue anyway?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, continue",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#f59e0b",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    const formData = new FormData();
    formData.append("codigo", code.trim() || "");
    formData.append("nombre", name);
    formData.append("precio_venta", hasPresentations ? 0 : salePrice);
    formData.append("precio_compra", parseInt(itemType) === 2 ? "" : purchasePrice || "");
    formData.append(
      "stock",
      parseInt(itemType) === 2 ? 0 : presentationType === "variante" && hasPresentations ? 0 : stock
    );
    formData.append("stock_minimo", parseInt(itemType) === 2 ? 0 : minStock);
    formData.append("is_taxable", !taxExempt ? 1 : 0);
    formData.append("tipo_item", parseInt(itemType));
    formData.append("descripcion", description || "");
    formData.append("categoria", category);
    if (photoFile) formData.append("foto", photoFile);
    if (removeExistingPhoto) formData.append("quitar_foto", 1);
    if (hasPresentations) {
      formData.append(
        "presentations",
        JSON.stringify(
          presentations.map((p) => ({
            descripcion: p.descripcion,
            precio_compra: presentationType === "variante" ? parseFloat(p.precio_compra) : null,
            precio_venta: parseFloat(p.precio_venta),
            factor_conversion: presentationType === "empaque" ? parseInt(p.factor_conversion) : 1,
            tipo_presentacion: presentationType,
            stock_independiente:
              presentationType === "variante" ? parseInt(p.stock_independiente) : null,
          }))
        )
      );
    }

    try {
      if (editingProduct) {
        formData.append("_method", "PUT");
        await api.post(`/products/${editingProduct.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Updated", "Product updated successfully", "success");
      } else {
        await api.post("/products", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Saved", "Product created successfully", "success");
      }

      resetForm();
      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (error) {
      console.error("ERROR:", error);
      Swal.fire("Error", error.response?.data?.message || "Error saving the product", "error");
    }
  };

  const isService = parseInt(itemType) === 2;

  const calculateMargin = () => {
    if (hasPresentations || !purchasePrice || !salePrice || isService) return null;
    const cost = parseFloat(purchasePrice);
    const price = parseFloat(salePrice);
    if (cost === 0) return null;
    return (((price - cost) / cost) * 100).toFixed(2);
  };

  const margin = calculateMargin();

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingProduct ? "Edit Product/Service" : "New Product/Service"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Basic Information
          </h4>

          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "16px", marginBottom: "16px" }}>
            <div className="form-group">
              <label className="form-label">Code</label>
              <input
                type="text"
                className="form-control"
                placeholder="Scan or type (optional)"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                ref={codeRef}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Product/Service Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter the name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-control"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <small style={{ color: "#718096", fontSize: "12px" }}>
                Section where it will appear on the menu
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Image size={14} style={{ display: "inline", marginRight: "4px" }} />
                Product photo
              </label>

              {photoPreview ? (
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: "72px",
                      height: "72px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                    }}
                  />
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => photoInputRef.current?.click()}
                      style={{ display: "block", marginBottom: "6px" }}
                    >
                      Change photo
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={removePhoto}>
                      Remove photo
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    border: "2px dashed #cbd5e0",
                    borderRadius: "8px",
                    padding: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "#fafafa",
                  }}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Image size={28} color="#a0aec0" style={{ marginBottom: "6px" }} />
                  <p style={{ margin: 0, color: "#718096", fontSize: "13px" }}>Click to upload photo</p>
                  <p style={{ margin: "2px 0 0", color: "#a0aec0", fontSize: "11px" }}>
                    JPG, PNG — max 2MB
                  </p>
                </div>
              )}

              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">
                <Tag size={14} style={{ display: "inline", marginRight: "4px" }} />
                Item Type *
              </label>
              <select
                className="form-control"
                value={itemType}
                onChange={(e) => {
                  setItemType(e.target.value);
                  if (e.target.value === "2") {
                    setHasPresentations(false);
                    setPresentations([]);
                  }
                }}
                required
              >
                <option value={1}>Product</option>
                <option value={2}>Service</option>
              </select>
              <small style={{ color: "#718096", fontSize: "12px" }}>
                {isService
                  ? "Services don't track inventory or purchase cost"
                  : "Products track inventory and cost"}
              </small>
            </div>
          </div>

          {!isService && (
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "24px",
                border: "2px solid #0ea5e9",
              }}
            >
              <div className="form-check" style={{ marginBottom: "12px" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="hasPresentations"
                  checked={hasPresentations}
                  onChange={(e) => {
                    setHasPresentations(e.target.checked);
                    if (!e.target.checked) {
                      setPresentations([]);
                      setPresentationType("empaque");
                    }
                  }}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label"
                  htmlFor="hasPresentations"
                  style={{ cursor: "pointer", fontWeight: 600, color: "#0369a1" }}
                >
                  📦 Does this product have multiple presentations?
                </label>
                <small
                  style={{ display: "block", color: "#0369a1", fontSize: "12px", marginTop: "4px", marginLeft: "24px" }}
                >
                  Example: Coke by the unit, 12-pack, or 24-pack with different prices
                </small>
              </div>

              {hasPresentations && (
                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #0ea5e9" }}>
                  <label style={{ display: "block", fontWeight: 600, color: "#0369a1", marginBottom: "12px" }}>
                    What type of presentations does it have?
                  </label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div
                      style={{
                        background: presentationType === "empaque" ? "#e0f2fe" : "white",
                        padding: "12px",
                        borderRadius: "8px",
                        border: presentationType === "empaque" ? "2px solid #0284c7" : "2px solid #cbd5e0",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setPresentationType("empaque");
                        setPresentations([]);
                      }}
                    >
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="typePack"
                          name="presentationType"
                          value="empaque"
                          checked={presentationType === "empaque"}
                          onChange={(e) => {
                            setPresentationType(e.target.value);
                            setPresentations([]);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        <label className="form-check-label" htmlFor="typePack" style={{ cursor: "pointer", fontWeight: 500 }}>
                          📦 By pack/quantity (shared stock)
                        </label>
                        <small style={{ display: "block", color: "#0369a1", fontSize: "11px", marginTop: "4px", marginLeft: "24px" }}>
                          Example: Single, 6-pack, 12-pack, 24-pack. All share the same inventory.
                        </small>
                      </div>
                    </div>

                    <div
                      style={{
                        background: presentationType === "variante" ? "#e0f2fe" : "white",
                        padding: "12px",
                        borderRadius: "8px",
                        border: presentationType === "variante" ? "2px solid #0284c7" : "2px solid #cbd5e0",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setPresentationType("variante");
                        setPresentations([]);
                      }}
                    >
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="typeVariant"
                          name="presentationType"
                          value="variante"
                          checked={presentationType === "variante"}
                          onChange={(e) => {
                            setPresentationType(e.target.value);
                            setPresentations([]);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                        <label className="form-check-label" htmlFor="typeVariant" style={{ cursor: "pointer", fontWeight: 500 }}>
                          🏷️ By variant/flavor (independent stock)
                        </label>
                        <small style={{ display: "block", color: "#0369a1", fontSize: "11px", marginTop: "4px", marginLeft: "24px" }}>
                          Example: Chicken, Shrimp, Beef. Each flavor has its own inventory and barcode.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Prices &amp; Inventory
          </h4>

          {hasPresentations ? (
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Product Presentations
                </label>
                <button type="button" className="btn btn-primary btn-sm" onClick={addPresentation}>
                  <Plus size={16} />
                  Add Presentation
                </button>
              </div>

              {presentations.length === 0 ? (
                <div style={{ padding: "32px", textAlign: "center", background: "#f7fafc", borderRadius: "10px", border: "2px dashed #cbd5e0" }}>
                  <p style={{ color: "#718096", margin: 0 }}>
                    No presentations added yet. Click "Add Presentation" to start.
                  </p>
                </div>
              ) : (
                <div className="table-container" style={{ overflowX: "auto" }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "20%" }}>Description *</th>
                        {presentationType === "variante" && <th style={{ width: "15%" }}>Purchase Price *</th>}
                        <th style={{ width: "15%" }}>Sale Price *</th>
                        {presentationType === "empaque" ? (
                          <th style={{ width: "15%" }}>Conversion Factor *</th>
                        ) : (
                          <th style={{ width: "15%" }}>Stock *</th>
                        )}
                        <th style={{ width: "10%", textAlign: "center" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {presentations.map((pres, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={presentationType === "empaque" ? 'E.g. "Single", "12-Pack"' : 'E.g. "Chicken", "Shrimp"'}
                              value={pres.descripcion}
                              onChange={(e) => updatePresentation(index, "descripcion", e.target.value)}
                              required
                            />
                          </td>
                          {presentationType === "variante" && (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                value={pres.precio_compra || ""}
                                onChange={(e) => updatePresentation(index, "precio_compra", e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>Cost</small>
                            </td>
                          )}
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              value={pres.precio_venta}
                              onChange={(e) => updatePresentation(index, "precio_venta", e.target.value)}
                              required
                            />
                          </td>
                          {presentationType === "empaque" ? (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="1"
                                min="1"
                                value={pres.factor_conversion}
                                onChange={(e) => updatePresentation(index, "factor_conversion", e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>Base units</small>
                            </td>
                          ) : (
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="0"
                                min="0"
                                value={pres.stock_independiente}
                                onChange={(e) => updatePresentation(index, "stock_independiente", e.target.value)}
                                required
                              />
                              <small style={{ color: "#718096", fontSize: "11px" }}>Own stock</small>
                            </td>
                          )}
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removePresentation(index)}
                              title="Remove presentation"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ background: "#fffbeb", padding: "12px", borderRadius: "8px", border: "1px solid #fbbf24", marginTop: "12px" }}>
                <small style={{ color: "#92400e", fontSize: "12px" }}>
                  {presentationType === "empaque" ? (
                    <>
                      💡 <strong>Conversion factor:</strong> Indicates how many base units this presentation represents.
                      For example, a 12-pack has a factor of 12, a 24-pack has a factor of 24.
                    </>
                  ) : (
                    <>
                      💡 <strong>Independent stock:</strong> Each variant tracks its own inventory.
                      For example, if you have 50 units of Chicken and 30 of Shrimp, they're separate stocks.
                    </>
                  )}
                </small>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isService ? "1fr 1fr 1fr" : "1fr 1fr 1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              {!isService && (
                <div className="form-group">
                  <label className="form-label">
                    <ShoppingCart size={14} style={{ display: "inline", marginRight: "4px" }} />
                    Purchase Price *
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required={!isService}
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>Acquisition cost</small>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  <DollarSign size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Sale Price *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>Price to the customer</small>
              </div>

              {!isService && margin !== null && (
                <div className="form-group">
                  <label className="form-label">Margin</label>
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: parseFloat(margin) >= 0 ? "#d1fae5" : "#fee2e2",
                      color: parseFloat(margin) >= 0 ? "#065f46" : "#991b1b",
                      borderRadius: "6px",
                      fontWeight: 600,
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {margin}%
                  </div>
                  <small style={{ color: "#718096", fontSize: "12px" }}>Estimated profit</small>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  <Boxes size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Current Stock *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={isService ? "-" : "0"}
                  min="0"
                  required
                  value={isService ? "" : stock}
                  onChange={(e) => setStock(e.target.value)}
                  disabled={isService}
                  style={{ backgroundColor: isService ? "#f7fafc" : "white", color: isService ? "#a0aec0" : "inherit" }}
                />
                {isService && <small style={{ color: "#718096", fontSize: "12px" }}>Not applicable</small>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <AlertCircle size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Min Stock
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={isService ? "-" : "5"}
                  min="0"
                  value={isService ? "" : minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  disabled={isService}
                  style={{ backgroundColor: isService ? "#f7fafc" : "white", color: isService ? "#a0aec0" : "inherit" }}
                />
                {!isService && <small style={{ color: "#718096", fontSize: "12px" }}>Restock alert</small>}
              </div>
            </div>
          )}

          {!isService && hasPresentations && presentationType === "empaque" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
              <div className="form-group">
                <label className="form-label">
                  <ShoppingCart size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Purchase Price (Unit) *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>Cost per base unit</small>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Boxes size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Current Stock *
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  min="0"
                  required
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>Units in inventory</small>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <AlertCircle size={14} style={{ display: "inline", marginRight: "4px" }} />
                  Min Stock
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="5"
                  min="0"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                />
                <small style={{ color: "#718096", fontSize: "12px" }}>Restock alert</small>
              </div>
            </div>
          )}

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <div className="form-group" style={{ marginBottom: "24px" }}>
            <label className="form-label">Description (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Additional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div style={{ background: "#f7fafc", padding: "16px", borderRadius: "10px", marginBottom: "24px" }}>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="taxExempt"
                checked={taxExempt}
                onChange={(e) => setTaxExempt(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <label className="form-check-label" htmlFor="taxExempt" style={{ cursor: "pointer", fontWeight: 500 }}>
                {isService ? "Service" : "Product"} exempt from sales tax
              </label>
              <small style={{ display: "block", color: "#718096", fontSize: "12px", marginTop: "4px", marginLeft: "24px" }}>
                Check this if sales tax does not apply
              </small>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              {editingProduct ? "Update" : "Save"}
            </button>

            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}